/* ai-prints-pdp.js — buy-box controller for templates/product.ai-prints.json.
 *
 * Drives the morphing CTA, the mobile sticky bar, the price on both, and the
 * launch analytics. It reads state from the AI Prints app block
 * (#pod-pdp-root, pod-pdp-controller.js) purely through the DOM — which of the
 * app's controls are visible — and acts by clicking the app's own buttons, so
 * the app's upload / preview / approve / add-to-cart path, including every
 * line item property it writes, runs exactly as before.
 *
 * State map (app → this template):
 *   [data-pod-retry] visible                       → retry
 *   [data-pod-actions] visible, approve has --done → added   (4s confirmation)
 *   [data-pod-actions] visible                     → ready   (Add to Cart + price)
 *   [data-pod-progress] visible                    → generating
 *   otherwise (idle, app loading, guided fallback) → upload
 */
(function () {
  'use strict';

  var COPY = {
    upload: {
      label: "🐶 Upload your pet's photo",
      sub: 'No payment required. See your design before you buy.'
    },
    generating: {
      label: 'Creating your preview… about 60 seconds',
      sub: 'Browse backgrounds while you wait'
    },
    // The price is no longer part of the label: every button carries its own
    // price group (snippets/pod-pdp-price.liquid) with the compare-at price
    // struck through, shown in the states listed in PRICED_STATES.
    ready: { label: 'Add to Cart', sub: '' },
    working: { label: 'Adding to cart…', sub: 'Saving your design — this only takes a moment.' },
    added: { label: 'Added to cart ✓', sub: '' },
    retry: {
      label: 'Try again',
      sub: 'That photo did not go through. Tap to retry, or choose a different photo.'
    }
  };
  var NAME_LABEL = "Add your pet's name (optional)";
  // pod: embedded-name order v2 (docs/theme-patches/pdp-embedded-name-order.md).
  // On a template whose prompt draws the pet's name into the art, the app
  // marks its name field data-pod-name-mode="embedded", and NAME_LABEL (which
  // says "optional") is never put on it. The theme's own field above the CTA
  // carries the label, whose one copy is ap_name_label in
  // snippets/ai-prints-cta.liquid (read back here from data-ap-name-label).
  // The app's in-root field, shown only when that metafield is not set, keeps
  // the app's own label ("Pet's name").
  // States whose buttons show the price. The rest are progress/status labels
  // ("Creating your preview…", "Adding to cart…", "Added ✓", "Try again")
  // where a price would only crowd a phone-width button.
  var PRICED_STATES = { upload: true, ready: true };

  function init() {
    var wrap = document.querySelector('[data-ap-cta-wrap]');
    var cta = document.querySelector('[data-ap-cta]');
    if (!cta) return;
    var sub = document.querySelector('[data-ap-cta-sub]');
    var sticky = document.querySelector('[data-ap-sticky]');
    var stickyBtn = sticky ? sticky.querySelector('[data-ap-sticky-btn]') : null;
    // Every purchase button on the page runs the same state machine: the top
    // CTA, any lower mirror (snippets/pod-pdp-atc.liquid), and the sticky bar.
    var mirrors = [].slice.call(document.querySelectorAll('[data-ap-cta-mirror]'));
    var buttons = [cta].concat(mirrors);
    if (stickyBtn) buttons.push(stickyBtn);
    var sizeBox = document.querySelector('[data-ap-size]');
    var productId = wrap ? wrap.getAttribute('data-product-id') : '';

    var state = 'upload';
    var sawGenerating = false;
    var previewTracked = false;

    // Fixed positioning must not be trapped by a transformed ancestor.
    if (sticky && sticky.parentNode !== document.body) document.body.appendChild(sticky);

    function root() { return document.getElementById('pod-pdp-root'); }
    function q(sel) { var r = root(); return r ? r.querySelector(sel) : null; }
    function shown(el) {
      if (!el) return false;
      for (var n = el; n; n = n.parentElement) { if (n.hidden) return false; }
      return true;
    }
    function selectedRadio() { return sizeBox ? sizeBox.querySelector('input[name="id"]:checked') : null; }
    function price() { var r = selectedRadio(); return r ? (r.getAttribute('data-price') || '') : ''; }
    function priceValue() {
      var r = selectedRadio();
      var cents = r ? parseInt(r.getAttribute('data-price-cents'), 10) : NaN;
      return isNaN(cents) ? undefined : cents / 100;
    }
    function variantId() { var r = selectedRadio(); return r ? r.value : ''; }
    // Compare-at price of the selected variant, or '' when it has none. The
    // options snippet only emits data-compare-price when compare > price.
    function comparePrice() {
      var r = selectedRadio();
      if (!r) return '';
      var cmp = parseInt(r.getAttribute('data-compare-cents'), 10);
      var cur = parseInt(r.getAttribute('data-price-cents'), 10);
      if (isNaN(cmp) || isNaN(cur) || cmp <= cur) return '';
      return r.getAttribute('data-compare-price') || '';
    }

    function setHidden(el, hide) { if (el) el.hidden = !!hide; }
    function renderPrice(btn, showPrice) {
      var group = btn.querySelector('[data-ap-price]');
      if (!group) return;
      var now = price();
      var was = comparePrice();
      group.hidden = !showPrice || !now;
      var nowEl = group.querySelector('[data-ap-price-now]');
      var wasEl = group.querySelector('[data-ap-price-compare]');
      if (nowEl && now) nowEl.textContent = now;
      if (wasEl) wasEl.textContent = was;
      setHidden(wasEl, !was);
      setHidden(group.querySelector('[data-ap-price-compare-label]'), !was);
      setHidden(group.querySelector('[data-ap-price-now-label]'), !was);
    }
    function background() {
      var v = q('.pod-bg-picker__value');
      return v ? v.textContent.trim() : '';
    }

    function computeState() {
      // pod: a photo chosen through the app block's early input is waiting for
      // the app to mount: show the work in progress, not an invitation to tap.
      if (window.PodEarlyUpload && window.PodEarlyUpload.held) return 'generating';
      var r = root();
      if (!r || r.hidden) return 'upload';
      if (shown(q('[data-pod-retry]'))) return 'retry';
      if (shown(q('[data-pod-actions]'))) {
        var approve = q('[data-pod-approve]');
        // The app disables its own button while a save is in flight. The click
        // proxy below already respects that (a disabled button ignores
        // .click()); without this the CTA looked pressable and swallowed it.
        if (approve && approve.disabled) return 'working';
        return approve && approve.classList.contains('pod-pdp__approve--done') ? 'added' : 'ready';
      }
      if (shown(q('[data-pod-progress]'))) return 'generating';
      return 'upload';
    }

    function render() {
      state = computeState();
      var copy = COPY[state];
      var label = copy.label.replace('{price}', price());
      var busy = state === 'generating' || state === 'working';
      var priced = !!PRICED_STATES[state];

      buttons.forEach(function (btn) {
        var lbl = btn.querySelector('[data-ap-cta-label], [data-ap-sticky-label]');
        if (lbl) lbl.textContent = label;
        btn.setAttribute('data-ap-state', state);
        btn.disabled = busy;
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
        renderPrice(btn, priced);
      });
      // A knit has no backgrounds: no "Browse backgrounds while you wait".
      var r0 = root();
      var subText = state === 'generating' && r0 && r0.hasAttribute('data-pod-knit') ? '' : copy.sub;
      if (sub) { sub.textContent = subText; sub.hidden = !subText; }

      // Launch KPI: preview render completed and is on screen. Fired once per
      // run, only after this page actually watched the run (a rehydrated
      // session that loads straight into "ready" is not a fresh preview).
      if (state === 'generating') { sawGenerating = true; previewTracked = false; }
      if (state === 'upload') { sawGenerating = false; previewTracked = false; }
      if (state === 'ready' && sawGenerating && !previewTracked) {
        previewTracked = true;
        track('ap_preview_viewed', {});
      }
    }

    function relabelName() {
      var section = q('[data-pod-name-section]');
      var label = section ? section.querySelector('.pod-pdp__label') : null;
      if (section && section.getAttribute('data-pod-name-mode') === 'embedded') return;
      if (label && label.textContent !== NAME_LABEL) label.textContent = NAME_LABEL;
    }

    // pod: embedded-name order v2. The theme's name field above the CTA.
    function syncNameField() {
      var field = document.querySelector('[data-pod-name-external]');
      if (!field) return;
      var r = root();
      var mode = field.getAttribute('data-pod-name-mode');
      // The app un-hides its root and claims the field (sets the mode) in one
      // task. A visible root with an unclaimed field means an older app that
      // ignores it: hide it rather than show a second, dead name field.
      var dead = !!(r && !r.hidden && mode === null);
      if (dead && !field.hasAttribute('data-ap-name-dead')) {
        field.setAttribute('data-ap-name-dead', '');
        field.hidden = true;
      } else if (!dead && field.hasAttribute('data-ap-name-dead')) {
        field.removeAttribute('data-ap-name-dead');
        field.hidden = false;
      }
      // A doc whose model does not draw the name (a stale metafield) gets the
      // ordinary label; otherwise the snippet's own.
      var label = field.querySelector('.ap-name__label');
      var want = mode && mode !== 'embedded' ? NAME_LABEL : field.getAttribute('data-ap-name-label');
      if (label && want && label.textContent !== want) label.textContent = want;
    }

    function openPicker() {
      var r = root();
      if (r && !r.hidden) {
        var btn = r.querySelector('[data-pod-upload]');
        var input = r.querySelector('[data-pod-file]');
        if (btn && shown(btn)) { btn.click(); return; }   // display:none by CSS, still clickable
        if (input) { input.click(); return; }
      }
      // Guided-modal fallback (the app reverts to it if its auto flow can't load).
      var legacy = document.querySelector('[data-pet-designer-open]');
      if (legacy && legacy.style.display !== 'none') { legacy.click(); return; }
      // pod: the early input (app block) can still open the chooser.
      if (window.PodEarlyUpload && window.PodEarlyUpload.open()) return;
      // App still initialising: bring it into view; the next tap will work.
      if (r) r.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function onCtaClick(evt) {
      evt.preventDefault();
      switch (state) {
        case 'ready':
        case 'added': {
          var approve = q('[data-pod-approve]');
          if (approve) approve.click();
          break;
        }
        case 'retry': {
          var retry = q('[data-pod-retry]');
          if (retry) retry.click();
          break;
        }
        case 'generating':
          break;
        default:
          openPicker();
      }
    }
    buttons.forEach(function (btn) { btn.addEventListener('click', onCtaClick); });
    // pod: early upload. The held photo changes the state without touching the app's DOM.
    ['pod:early-file', 'pod:early-disarm'].forEach(function (name) {
      document.addEventListener(name, render);
    });

    // Size → price on both buttons. The app reads `[name="id"]:checked` for
    // the variant and hears the same change event; any /cart/add form an app
    // may inject is kept in sync too because the app prefers those.
    if (sizeBox) {
      sizeBox.addEventListener('change', function (evt) {
        if (!evt.target || evt.target.name !== 'id') return;
        var forms = document.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
        for (var i = 0; i < forms.length; i++) forms[i].value = evt.target.value;
        render();
      });
    }

    // Launch KPI: upload begun (a photo was chosen). Capture phase so it runs
    // before the app's own change handler clears the input value.
    document.addEventListener('change', function (evt) {
      var t = evt.target;
      if (!t || !t.matches || !t.matches('[data-pod-file]')) return;
      if (!t.files || !t.files.length) return;
      track('ap_upload_started', { method: 'file_selected', file_type: t.files[0].type || '' });
    }, true);

    function track(name, params) {
      var payload = {
        event: name,
        product_id: productId,
        variant_id: variantId(),
        price: priceValue(),
        currency: 'USD',
        background: background()
      };
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      } catch (e) { /* analytics must never break the buy box */ }
      try { document.dispatchEvent(new CustomEvent('ap:' + name, { detail: payload })); } catch (e) { /* noop */ }
    }

    // Sticky bar: visible on mobile only while NO on-page purchase button (top
    // CTA or lower mirror) is on screen, so two buttons never show at once.
    if (sticky) {
      if ('IntersectionObserver' in window) {
        var onScreen = [];
        var watched = [cta].concat(mirrors);
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            var i = watched.indexOf(e.target);
            if (i !== -1) onScreen[i] = e.isIntersecting;
          });
          var inView = onScreen.some(Boolean);
          sticky.hidden = false;
          sticky.classList.toggle('ap-sticky--off', inView);
          document.documentElement.classList.toggle('ap-has-sticky', !inView);
        }, { threshold: 0.5 });
        watched.forEach(function (el) { io.observe(el); });
      } else {
        sticky.hidden = false;
        document.documentElement.classList.add('ap-has-sticky');
      }
    }

    // Follow the app: it toggles `hidden` / classes on its controls as its
    // stage changes, and may render its skeleton after we run.
    var observer = new MutationObserver(function () { relabelName(); syncNameField(); render(); });
    var r = root();
    if (r) {
      observer.observe(r, { attributes: true, attributeFilter: ['hidden', 'class', 'disabled', 'data-pod-name-mode', 'data-pod-knit'], childList: true, subtree: true });
    }
    var nameField = document.querySelector('[data-pod-name-external]');
    if (nameField) observer.observe(nameField, { attributes: true, attributeFilter: ['data-pod-name-mode'] });
    relabelName();
    syncNameField();
    render();
  }

  // Backgrounds in the page, not in a popup. The app renders 10 swatches plus a
  // "See all" bottom sheet; on this template every background is laid out in
  // wrapping rows instead, so the preview never greys out and switching never
  // reopens a sheet. Runtime-only: the picker's strip limit is widened just
  // while its strip renders (the app's own prefetch keeps its 10-image
  // budget), and the app's files are untouched. If the app ever renames
  // _renderStrip the patch is skipped and the stock "See all" sheet remains.
  function inlineBackgrounds(Picker) {
    if (!Picker || !Picker.prototype || Picker.prototype.__apInline) return Picker;
    var renderStrip = Picker.prototype._renderStrip;
    if (typeof renderStrip !== 'function') return Picker;
    Picker.prototype._renderStrip = function () {
      var limit = this.stripLimit;
      this.stripLimit = Infinity;
      try { return renderStrip.apply(this, arguments); }
      finally {
        this.stripLimit = limit;
        if (this.root && this.root.classList) this.root.classList.add('ap-bg-inline');
      }
    };
    Picker.prototype.__apInline = true;
    return Picker;
  }
  (function () {
    var current = window.PodBackgroundPicker;
    if (current) { inlineBackgrounds(current); return; }
    // The app injects its scripts after this one runs; catch the constructor
    // the moment the app publishes it.
    try {
      Object.defineProperty(window, 'PodBackgroundPicker', {
        configurable: true,
        enumerable: true,
        get: function () { return current; },
        set: function (v) { current = inlineBackgrounds(v); }
      });
    } catch (e) { /* non-configurable window prop: fall back to the stock sheet */ }
  })();

  // Fallback for the stock "See all" sheet (if the patch above did not apply):
  // keep the sticky bar out of the way while the sheet is open.
  if (document.body && 'MutationObserver' in window) {
    new MutationObserver(function () {
      var open = !!document.querySelector('.pod-bg-panel__overlay');
      document.documentElement.classList.toggle('ap-panel-open', open);
    }).observe(document.body, { childList: true });
  }

  // "Create a memory" UGC videos (snippets/ai-prints-ugc.liquid): click-to-play,
  // native controls appear once playing, and starting one pauses the others.
  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-ap-video] .ap-video__play') : null;
    if (!btn) return;
    var fig = btn.closest('[data-ap-video]');
    var video = fig ? fig.querySelector('video') : null;
    if (!video) return;
    var others = document.querySelectorAll('[data-ap-video] video');
    for (var i = 0; i < others.length; i++) if (others[i] !== video && !others[i].paused) others[i].pause();
    fig.classList.add('is-playing');
    video.controls = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () { /* autoplay policy: controls are visible, user can press play */ });
    video.addEventListener('ended', function () {
      fig.classList.remove('is-playing');
      video.controls = false;
    }, { once: true });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
/* pod: working-state patch */
/* pod: knit-no-backgrounds-sub patch */
/* pod: early-upload patch */
